/**
 * Habillage du terrain — source unique.
 *
 * Le filet avait six rendus et le demi-terrain cinq : rectangle gris d'un mètre
 * avec « NET » dans les graphiques, bande de 18 cm en cartographie, trait blanc
 * en saisie experte, bordure de 3 px sur le terrain tactile, trait d'un pixel
 * sur le mini-terrain du clavier, dégradé et pastille « Filet » en saisie
 * classique — et pas de filet du tout sur les grilles de composition. Les fonds
 * allaient du terracotta au bleu pâle en passant par le blanc.
 *
 * Tout le terrain suit la saisie experte : l'orange `#EAAD62` d'un seul tenant
 * (plus de zone avant plus foncée), des lignes blanches, la ligne des 3 m en
 * pointillés et le filet en trait blanc qui déborde des lignes de côté. C'est ce
 * que le coach a sous les yeux pendant le match ; les graphiques gardent un fond
 * de page neutre, mais le terrain y est le même.
 *
 * Les épaisseurs sont exprimées en MÈTRES, comme les terrains eux-mêmes : chaque
 * surface a son échelle, aucune n'a à convertir des pixels.
 */

export const COURT_STYLE = {
  /** Aire de jeu — une seule teinte, zone avant comprise, comme en saisie experte. */
  surface: '#EAAD62',
  /** Hors terrain / zone libre. */
  outside: '#e2e8f0',
  /** Fond de page d'un graphique : neutre, le terrain s'y détache. */
  chartBackground: '#f8fafc',
  /** Lignes du terrain. */
  line: '#ffffff',
  lineOpacity: 0.9,
  /** Ligne des 3 m : pointillés discrets, ceux de la saisie experte. */
  attackLineOpacity: 0.35,
  /** Filet : un trait blanc qui déborde des lignes de côté (saisie experte). */
  net: '#ffffff',
  netOpacity: 0.9,
  /** Filet posé sur un fond clair (graphiques) : le blanc y disparaîtrait. */
  netOnLight: '#64748b',
  /**
   * Terrain des graphiques d'analyse : clair, lignes grises. Sur l'orange, les
   * flèches colorées par qualité (vert, jaune, orange, rouge) ne se lisaient plus.
   */
  chartSurface: '#ffffff',
  chartLine: '#94a3b8',
} as const;

/** Demi-terrain réglementaire, en mètres. */
export const COURT_WIDTH_M = 9;
export const COURT_HALF_LENGTH_M = 9;

/** Profondeur de la zone avant, du filet à la ligne des 3 m. */
export const FRONT_ZONE_DEPTH_M = 3;

/** Épaisseur du trait de filet, en mètres (celle de la saisie experte). */
export const NET_BAND_M = 0.14;

/** Débord du filet au-delà de chaque ligne de côté, en mètres. */
export const NET_OVERHANG_M = 0.5;

/** Pointillés de la ligne des 3 m, en mètres : trait puis espace. */
export const ATTACK_LINE_DASH_M = [0.25, 0.15] as const;

/** Épaisseur d'une ligne de terrain, en mètres (5 cm réglementaires). */
export const COURT_LINE_M = 0.05;

/**
 * Épaisseur minimale visible, en mètres : sous cette valeur un trait à l'échelle
 * disparaît dans les petits rendus (mini-terrain du clavier, vignettes).
 */
export const MIN_VISIBLE_M = 0.03;

/** Classes Tailwind équivalentes, pour les surfaces dessinées en CSS. */
export const COURT_CLASS = {
  surface: 'bg-[#EAAD62]',
  outside: 'bg-slate-200',
  line: 'bg-white/90',
  net: 'bg-white/90',
} as const;
